// Utils & misc
import cn from '../../../utils/cn';

// Components
import MenuManagementRow from '../../molecules/MenuManagementRow/MenuManagementRow';

// Domain
import { getMenuItemCheckState } from '../../../domain/menu/menuManagementUseCase';
import { MENU_MAX_DEPTH, hasMenuChildren } from '../../../domain/menu/menuNavigationUseCase';

// Style
import style from './MenuManagementTree.module.css';

/** Levels rendered as collapsible sections (sidebar item, popover owner); deeper levels are always shown. */
const COLLAPSIBLE_DEPTH = 2;

/**
 * MenuManagementTree component - Recursive checkbox/star tree of the menu management modal.
 * @param {object} params
 * @param {ApiManagerMenuItem[]} params.items - Items of the current level
 * @param {number} [params.depth=1] - Level of `items` (1 = sidebar)
 * @param {Set<string>} params.expandedIds - Expanded collapsible items
 * @param {Set<string> | null} [params.matchingIds=null] - Search result: only these items are displayed, all expanded
 * @param {(id: string) => void} params.onExpandToggle - Expand toggle handler
 * @param {(id: string, available: boolean) => void} params.onAvailableChange - Checkbox handler
 * @param {(id: string, favorite: boolean) => void} params.onFavoriteChange - Star handler
 * @returns {JSX.Element | null} MenuManagementTree component
 */
function MenuManagementTree({
  items,
  depth = 1,
  expandedIds,
  matchingIds = null,
  onExpandToggle,
  onAvailableChange,
  onFavoriteChange,
}) {
  if (!items?.length || depth > MENU_MAX_DEPTH) return null;

  return (
    <ul
      className={cn([
        style.tree,
        depth > COLLAPSIBLE_DEPTH + 1 && style.nested,
        depth === COLLAPSIBLE_DEPTH + 1 && style.columns,
      ])}
    >
      {items.map((item) => {
        if (matchingIds && !matchingIds.has(item.id)) return null;

        const hasChildren = hasMenuChildren(item) && depth < MENU_MAX_DEPTH;
        const isCollapsible = hasChildren && depth <= COLLAPSIBLE_DEPTH;
        const isExpanded = !!matchingIds || expandedIds.has(item.id);
        const showChildren = hasChildren && (!isCollapsible || isExpanded);

        return (
          <li key={item.id} data-menu-item-id={item.id} className={style.item}>
            <MenuManagementRow
              id={`menu-management-${item.id}`}
              label={item.label}
              checkState={getMenuItemCheckState(item)}
              onCheckedChange={(checked) => onAvailableChange(item.id, checked)}
              favorite={hasMenuChildren(item) ? null : !!item.config?.favorite}
              onFavoriteChange={(favorite) => onFavoriteChange(item.id, favorite)}
              isExpanded={isCollapsible && !matchingIds ? isExpanded : null}
              onExpandToggle={() => onExpandToggle(item.id)}
            />
            {showChildren ? (
              <MenuManagementTree
                items={item.items}
                depth={depth + 1}
                expandedIds={expandedIds}
                matchingIds={matchingIds}
                onExpandToggle={onExpandToggle}
                onAvailableChange={onAvailableChange}
                onFavoriteChange={onFavoriteChange}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export default MenuManagementTree;
